import React, { useEffect, useMemo, useState } from 'react';
import { Box, Alert, Typography, Card, CardContent, List, ListItem, ListItemText, Button, TextField, Stack } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useVendorStatus } from '../../hooks/useVendorStatus';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { VendorSellThroughSharingCount } from '../../components/VendorSellThroughSharingCount';
import { makeApiCall, getAuthHeaders } from '../../utils/apiConfig';
import { brandHome } from './brandHomeTokens';
import { primaryContactName } from '../../utils/orgDisplayName';
import {
  useVendorMetricsSummary,
  useVendorConnectors,
  useVendorIngestionHistory,
  useMapViolationAlertsEnabled,
  useVendorMapViolations,
  useBrandRebateHistory,
  useVendorManagedBrands,
  type VendorConnector,
  type IngestionJobRow,
  type IngestionSummary,
  type EmailIngestionRecord,
  type ManagedBrandSummary,
} from './useBrandHomeData';
import { buildStoreViewModels, storeNeedsAttention, type StoreViewModel } from './storeViewModel';
import { deriveAttentionItems } from './deriveAttentionItems';
import { sortActivityRowsByDate, type ActivityRowWithRawDate } from './sortActivityRows';
import { BrandHeaderCard } from './components/BrandHeaderCard';
import { KpiRow } from './components/KpiRow';
import { AttentionQueueCard } from './components/AttentionQueueCard';
import { StoresCard } from './components/stores/StoresCard';
import { RebateFilesCard } from './components/rebates/RebateFilesCard';
import { ManagedBrandsCard } from './components/rebates/ManagedBrandsCard';
import { IngestionFeedCard } from './components/feed/IngestionFeedCard';
import { SyncHistoryCard } from './components/feed/SyncHistoryCard';
import { BrandActivityCard, type ActivityRow } from './components/activity/BrandActivityCard';
import { formatCatalogActivityText } from './formatCatalogActivity';
import { listVendorRebateRequests, VENDOR_REBATE_REQUESTS_QUERY_KEY } from '../../services/rebateRequestsApi';
import { DashboardEditBar } from '../dashboardShared/components/DashboardEditBar';
import { WidgetGrid } from '../dashboardShared/components/WidgetGrid';
import { useDashboardLayout } from '../dashboardShared/useDashboardLayout';

interface VendorApprovalRequest {
  id?: string;
  status?: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  evidence?: {
    dealerLetter?: { status?: string; fileName?: string; url?: string };
    dnsTxt?: { status?: string; token?: string; host?: string };
  };
  vendorNotes?: Array<{ userId: string; message: string; createdAt?: string }>;
}

const DEMO_KPIS = [
  { id: 'products', label: 'Catalog products', value: '847', sub: 'Demo data' },
  { id: 'synced_to_stores', label: 'Synced to stores', value: '812', sub: 'Demo data' },
  { id: 'excluded_by_stores', label: 'Not in any store', value: '35', sub: 'Demo data' },
  { id: 'stores_syncing', label: 'Stores syncing', value: '5', sub: 'Demo data' },
  { id: 'feed_success_rate', label: 'Feed success rate', value: '92%', sub: 'Demo data' },
];

const FINISHED_SETUP = {
  integrationConnected: true,
  setupIncomplete: false,
  setupSteps: [],
  authorizationApproved: true,
};

const DEMO_STORES: StoreViewModel[] = [
  { storeId: '1', storeName: "Mike's Camera", platforms: ['Shopify'], lastSyncAt: new Date().toISOString(), includedProductCount: 156, excludedProductCount: 4, countsArePartial: false, activeMapViolations: 0, issues: [], hasIssues: false, worstIssueSeverity: null, ...FINISHED_SETUP },
  { storeId: '2', storeName: 'Cardinal Camera', platforms: ['Shopify'], lastSyncAt: new Date(Date.now() - 5 * 3600 * 1000).toISOString(), includedProductCount: 134, excludedProductCount: 2, countsArePartial: false, activeMapViolations: 0, issues: [], hasIssues: false, worstIssueSeverity: null, ...FINISHED_SETUP },
  {
    storeId: '3',
    storeName: 'B&H Photo',
    platforms: ['Custom'],
    lastSyncAt: new Date(Date.now() - 200 * 3600 * 1000).toISOString(),
    includedProductCount: 201,
    excludedProductCount: 0,
    countsArePartial: false,
    activeMapViolations: 2,
    issues: [
      { severity: 'blocking', title: 'No sync in 8 days', meta: 'Catalog updates are not reaching this store.', age: '8d' },
      { severity: 'warning', title: '2 products priced under your MAP floor', meta: 'Pro Camera X1, Tripod T-200' },
    ],
    hasIssues: true,
    worstIssueSeverity: 'blocking',
    ...FINISHED_SETUP,
  },
];

const DEMO_REBATES: EmailIngestionRecord[] = [
  { id: 'd1', from: 'reporting@demobrand.com', subject: 'Rebates was executed at 9/7/2026', receivedAt: new Date().toISOString(), recordsParsed: 284, recordsSaved: 284, status: 'success' },
  { id: 'd2', from: 'Demo Ops', subject: 'Fwd: Rebates was executed at 9/4/2026', receivedAt: new Date(Date.now() - 3 * 86400000).toISOString(), recordsParsed: 312, recordsSaved: 284, status: 'partial' },
];

const DEMO_MANAGED_BRANDS: ManagedBrandSummary[] = [
  { brand: 'SmallRig', fileCount: 3, lastSubmittedAt: new Date().toISOString() },
  { brand: 'NANUK', fileCount: 2, lastSubmittedAt: new Date(Date.now() - 3 * 86400000).toISOString() },
  { brand: 'OM System', fileCount: 1, lastSubmittedAt: new Date(Date.now() - 12 * 86400000).toISOString() },
];

const DEMO_CONNECTOR: VendorConnector = {
  id: 'demo-connector',
  connectorName: 'Demo Brand API Connector',
  connectorType: 'api',
  status: 'active',
  dataDomains: ['accessories', 'lighting'],
  schedule: { frequency: 'weekly', lastRun: new Date(Date.now() - 8 * 3600 * 1000).toISOString(), nextRun: new Date(Date.now() + 6 * 86400000).toISOString() },
  handler: { endpoint: 'https://api.demobrand.com/v1/products' },
};

const DEMO_JOBS: IngestionJobRow[] = [
  { id: 'j1', status: 'completed', type: 'full_scrape', productsProcessed: 847, productsCreated: 12, productsUpdated: 30, errors: 0, createdAt: new Date(Date.now() - 8 * 3600 * 1000).toISOString(), completedAt: new Date(Date.now() - 8 * 3600 * 1000 + 300000).toISOString() },
  { id: 'j2', status: 'completed', type: 'price_refresh', productsProcessed: 412, productsCreated: 0, productsUpdated: 412, errors: 0, createdAt: new Date(Date.now() - 32 * 3600 * 1000).toISOString(), completedAt: new Date(Date.now() - 32 * 3600 * 1000 + 60000).toISOString() },
];

const DEMO_INGESTION_SUMMARY: IngestionSummary = {
  totalJobs: DEMO_JOBS.length,
  successRate: 100,
  avgSyncTime: 180000,
  lastSyncAt: DEMO_JOBS[0].completedAt || null,
  syncFrequency: 'weekly',
};

export const BrandHomePage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { userProfile } = useAuth();
  const {
    getEffectiveUserType,
    impersonatedBrand,
    impersonatedOrg,
    isImpersonating,
    impersonatedRole,
  } = useImpersonation();
  const queryClient = useQueryClient();
  const { organization, isPendingApproval: rawIsPendingApproval, isRejected, isActive, isLoading: vendorStatusLoading } = useVendorStatus();

  const effectiveUserType = getEffectiveUserType(userProfile);
  const [vendorNote, setVendorNote] = useState('');

  const isDemoMode =
    isImpersonating &&
    impersonatedRole === 'vendor' &&
    !impersonatedBrand?.id &&
    !impersonatedOrg?.id &&
    !organization?.id;

  // useVendorStatus defaults isPendingApproval to true while /organizations/me
  // is still in flight (it falls back to "pending" whenever `status` is
  // undefined). Trusting that default before loading finishes made an
  // approved vendor's cold load flash onboarding chrome and hide the real
  // dashboard for a frame. Only trust the pending/active read once we
  // actually know it.
  const statusKnown = isDemoMode || !vendorStatusLoading;
  const isPendingApproval = isDemoMode ? false : statusKnown && rawIsPendingApproval;

  const brandKey = organization?.id || impersonatedBrand?.id || '';
  const orgKey = impersonatedOrg?.id || 'self';
  const brandDisplayName =
    impersonatedBrand?.name || organization?.vendorInfo?.brandName || organization?.vendorName || organization?.name || 'Your Brand';

  const dataEnabled = !isDemoMode && effectiveUserType === 'vendor_supplier' && statusKnown && !isPendingApproval && !isRejected;

  const { data: approvalRequestResponse } = useQuery({
    queryKey: ['vendor-approval-request', organization?.id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor-approval/my-request-details', { headers });
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!organization && !isDemoMode,
  });
  const approvalRequest: VendorApprovalRequest | null = approvalRequestResponse?.data || null;
  const navDns = (location.state || {}) as { dnsToken?: string; dnsHost?: string };
  const dnsToken = approvalRequest?.evidence?.dnsTxt?.token || navDns.dnsToken;
  const dnsHost = approvalRequest?.evidence?.dnsTxt?.host || navDns.dnsHost;

  // The Application/Approval cards exist for the onboarding→approval journey
  // (and the #application deep link from notifications and org setup), not as
  // permanent chrome above an approved vendor's dashboard.
  const showApplicationCards =
    isPendingApproval || approvalRequest?.status === 'needs_info' || location.hash === '#application';

  const addVendorNoteMutation = useMutation({
    mutationFn: async (message: string) => {
      if (!approvalRequest?.id) throw new Error('Approval request not found');
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/vendor-approval/${approvalRequest.id}/vendor-note`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.message || 'Failed to send message');
      }
      return response.json();
    },
    onSuccess: () => {
      setVendorNote('');
      queryClient.invalidateQueries({ queryKey: ['vendor-approval-request', organization?.id] });
    },
  });

  const ensureApprovalRequestMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor-approval/ensure-request', { method: 'POST', headers });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.message || 'Failed to create approval request');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-approval-request', organization?.id] });
    },
  });

  // Scroll to the Application card once it exists — it mounts after `organization`
  // resolves, so a one-shot effect on route change can miss it.
  useEffect(() => {
    if (location.hash !== '#application') return;
    const target = document.getElementById('application');
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, organization, showApplicationCards]);

  const { enabled: sellThroughSharingEnabled } = useOrgFeatureFlag('sell_through_sharing');
  const { enabled: eventRebateRequestsEnabled } = useOrgFeatureFlag('event_rebate_requests');
  const rebateRequestsQuery = useQuery({
    queryKey: VENDOR_REBATE_REQUESTS_QUERY_KEY,
    queryFn: listVendorRebateRequests,
    enabled: eventRebateRequestsEnabled && dataEnabled && !isDemoMode,
  });
  const dash = useDashboardLayout('vendor', organization?.id, organization?.name);
  const summaryQuery = useVendorMetricsSummary(brandKey, dataEnabled);
  const connectorsQuery = useVendorConnectors(brandKey, dataEnabled);
  const ingestionHistoryQuery = useVendorIngestionHistory(brandKey, dataEnabled);
  const mapAlertsEnabledQuery = useMapViolationAlertsEnabled(orgKey, dataEnabled);
  const vendorId = summaryQuery.data?.vendorId;
  // MAP violation documents are keyed by the vendor's owning *organization id*
  // (see mapViolations.ts's ownership check), not the brand-slug `vendorId`
  // vendorMetricsService derives for catalog/connector reads — those are two
  // different identity spaces. Brand-only impersonation has no org id, so MAP
  // data simply stays unavailable in that case (the query is disabled below).
  const mapVendorId = organization?.id || impersonatedOrg?.id;
  const mapViolationsQuery = useVendorMapViolations(mapVendorId, dataEnabled && !!mapAlertsEnabledQuery.data);
  const rebateHistoryQuery = useBrandRebateHistory(orgKey, 20, dataEnabled);
  const managedBrandsQuery = useVendorManagedBrands(orgKey, dataEnabled);

  const summary = summaryQuery.data;
  const connectors = connectorsQuery.data || [];
  // Prefer the connector matching this brand's resolved vendorId and marked
  // active; a brand with more than one connector (e.g. separate US/CA feeds,
  // or an API connector alongside a scraper) would otherwise always show
  // whichever happened to load first.
  const connector =
    connectors.find((c) => c.status === 'active' && (!vendorId || c.vendorId === vendorId)) ||
    connectors.find((c) => c.status === 'active') ||
    connectors[0] ||
    null;
  const jobs = ingestionHistoryQuery.data?.jobs || [];
  const ingestionSummary = ingestionHistoryQuery.data?.summary || null;
  const mapViolations = mapViolationsQuery.data || [];
  const rebateRecords = rebateHistoryQuery.data || [];
  const managedBrands = managedBrandsQuery.data || [];

  // Store sync health (syncing / stale / never synced / setting up) comes from
  // the server's shared definition, so the store rows, the attention queue and
  // the "Stores syncing" KPI all use the same staleness window.
  const stores = useMemo(
    () => buildStoreViewModels(summary?.storeSyncs || [], mapViolations),
    [summary, mapViolations]
  );

  // Demo mode never calls live APIs, so every section below reads these
  // fixtures instead of hiding — a "preview" page with only a header and KPI
  // stubs doesn't actually preview the dashboard.
  const effectiveStores = isDemoMode ? DEMO_STORES : stores;
  const effectiveRebates = isDemoMode ? DEMO_REBATES : rebateRecords;
  const effectiveConnector = isDemoMode ? DEMO_CONNECTOR : connector;
  const effectiveJobs = isDemoMode ? DEMO_JOBS : jobs;
  const effectiveIngestionSummary = isDemoMode ? DEMO_INGESTION_SUMMARY : ingestionSummary;
  const storesNeedingAttention = useMemo(
    () => effectiveStores.filter(storeNeedsAttention).length,
    [effectiveStores]
  );
  const storesStillSettingUp = useMemo(
    () => effectiveStores.filter((s) => s.setupIncomplete).length,
    [effectiveStores]
  );

  // The queue lists exactly the stores `storeNeedsAttention` counts (one item
  // per store, from the same issues), plus feed and rebate problems.
  const attention = useMemo(
    () =>
      deriveAttentionItems({
        stores: effectiveStores.map((s) => ({
          storeId: s.storeId,
          storeName: s.storeName,
          issues: s.issues,
        })),
        jobs: effectiveJobs.map((j) => ({ status: j.status, createdAt: j.createdAt, completedAt: j.completedAt })),
        rebates: effectiveRebates.map((r) => ({ subject: r.subject, recordsSaved: r.recordsSaved, recordsParsed: r.recordsParsed, status: r.status })),
        rebateRequests: eventRebateRequestsEnabled ? (rebateRequestsQuery.data ?? []).map((r) => ({ status: r.status })) : [],
      }),
    [effectiveStores, effectiveJobs, effectiveRebates, eventRebateRequestsEnabled, rebateRequestsQuery.data]
  );

  const activityRows: ActivityRow[] = useMemo(() => {
    const rows: ActivityRowWithRawDate[] = [];
    const completedJob = effectiveJobs.find((j) => j.status === 'completed');
    if (completedJob) {
      rows.push({
        id: `job-${completedJob.id}`,
        tag: 'Catalog',
        tagBg: brandHome.color.chipFill,
        tagInk: brandHome.color.bodyAlt,
        text: formatCatalogActivityText(
          completedJob,
          effectiveConnector?.connectorName,
          summary?.totalProducts
        ),
        when: completedJob.createdAt ? new Date(completedJob.createdAt).toLocaleDateString() : '',
        rawDate: completedJob.createdAt,
      });
    }
    effectiveRebates.forEach((r) => {
      rows.push({
        id: `rebate-${r.id}`,
        tag: 'Rebate',
        tagBg: brandHome.color.cautionTint,
        tagInk: brandHome.color.cautionInk,
        text: r.subject || 'Rebate file processed',
        when: new Date(r.receivedAt).toLocaleDateString(),
        rawDate: r.receivedAt,
      });
    });
    // Newest first across every type, then cap at the feed's previous size
    // (one catalog row plus up to three rebates).
    return sortActivityRowsByDate(rows).slice(0, 4);
  }, [effectiveJobs, effectiveRebates, effectiveConnector, summary?.totalProducts]);

  if (effectiveUserType !== 'vendor_supplier') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" color="error">Access Denied</Typography>
        <Typography variant="body1">This page is only accessible to vendor and supplier accounts.</Typography>
      </Box>
    );
  }

  if (isRejected && !isDemoMode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>Account Locked</Typography>
          <Typography variant="body1">Your vendor application has been rejected. Your account access has been suspended.</Typography>
          {approvalRequest?.rejectionReason && (
            <Typography variant="body2" sx={{ mt: 1 }}>Reason: {approvalRequest.rejectionReason}</Typography>
          )}
        </Alert>
      </Box>
    );
  }

  const isPromasterBrand =
    String(summary?.vendorId || impersonatedBrand?.id || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') === 'promaster' ||
    String(brandDisplayName)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') === 'promaster';

  // One catalog size for the whole page: the products stores pick from. Every
  // store row's included + excluded, "Synced to stores" and "Not in any store"
  // are measured against it (server: brandDashboardMetrics.reconcileBrandCatalog).
  const catalogProducts = summary?.catalogProducts ?? summary?.totalProducts ?? 0;
  const ingestedProducts = summary?.totalProducts ?? 0;
  const storesCarrying = summary?.storesCarrying ?? summary?.storeSyncs?.length ?? 0;
  const storesSyncing = summary?.storesSyncing ?? 0;
  const syncWindowDays = summary?.syncStaleAfterDays;

  const kpiLabel = (id: string) => (t as unknown as (key: string) => string)(`dashboardLayout.kpis.vendor.${id}.label`);
  const kpis = isDemoMode
    ? DEMO_KPIS
    : [
        {
          id: 'products',
          label: isPromasterBrand ? 'ProMaster products' : kpiLabel('products'),
          value: catalogProducts.toLocaleString(),
          sub: [
            isPromasterBrand ? 'Your branded catalog' : 'Your catalog stores can sync',
            ingestedProducts !== catalogProducts ? `${ingestedProducts.toLocaleString()} ingested records` : '',
          ]
            .filter(Boolean)
            .join(' · '),
        },
        ...(isPromasterBrand
          ? [
              {
                id: 'warehouse_products',
                label: kpiLabel('warehouse_products'),
                value: (summary?.warehouseProducts ?? 0).toLocaleString(),
                sub: 'All brands you distribute',
              },
            ]
          : []),
        {
          id: 'synced_to_stores',
          label: kpiLabel('synced_to_stores'),
          value: (summary?.includedProducts ?? 0).toLocaleString(),
          sub: catalogProducts
            ? `${Math.round(((summary?.includedProducts ?? 0) / catalogProducts) * 100)}% of your catalog is in at least one store`
            : '',
        },
        {
          id: 'excluded_by_stores',
          label: kpiLabel('excluded_by_stores'),
          value: (summary?.excludedProducts ?? 0).toLocaleString(),
          sub:
            storesCarrying > 0
              ? `Not selected by any of your ${storesCarrying.toLocaleString()} store${storesCarrying === 1 ? '' : 's'}`
              : 'No store carries your brand yet',
          ink: brandHome.color.cautionInk,
        },
        {
          id: 'stores_syncing',
          label: kpiLabel('stores_syncing'),
          value: storesSyncing.toLocaleString(),
          sub:
            [
              `of ${storesCarrying.toLocaleString()} carrying your brand`,
              syncWindowDays ? `synced in the last ${syncWindowDays} days` : '',
              storesNeedingAttention > 0 ? `${storesNeedingAttention} need attention` : '',
              storesStillSettingUp > 0 ? `${storesStillSettingUp} still setting up` : '',
            ]
              .filter(Boolean)
              .join(' · '),
          ink: storesNeedingAttention > 0 ? brandHome.color.problemInk : undefined,
        },
        {
          id: 'feed_success_rate',
          label: kpiLabel('feed_success_rate'),
          value: ingestionSummary ? `${Math.round(ingestionSummary.successRate)}%` : '—',
          sub: ingestionSummary ? t('dashboard.recentSyncJobs', { count: ingestionSummary.totalJobs }) : 'No sync jobs yet',
          ink: ingestionSummary && ingestionSummary.successRate < 90 ? brandHome.color.cautionInk : undefined,
        },
      ];

  const statusLabel = isDemoMode ? 'Active' : isActive ? 'Active' : organization?.status || 'Active';

  const customizable = dash.enabled && !isDemoMode;
  const headerCardNode = (
        <BrandHeaderCard
            brandName={brandDisplayName}
            statusLabel={statusLabel}
            subline={
              isDemoMode
                ? '847 products ingested · 5 stores syncing · 812 live'
                : isPromasterBrand
                  ? `${catalogProducts.toLocaleString()} ProMaster products · ${(summary?.warehouseProducts ?? 0).toLocaleString()} warehouse · ${storesSyncing.toLocaleString()} of ${storesCarrying.toLocaleString()} stores syncing`
                  : `${catalogProducts.toLocaleString()} catalog products · ${storesSyncing.toLocaleString()} of ${storesCarrying.toLocaleString()} stores syncing · ${(summary?.includedProducts ?? 0).toLocaleString()} live`
            }
            meta={[
              { label: 'Company', value: organization?.vendorInfo?.companyName || organization?.name || brandDisplayName },
              { label: 'Primary contact', value: primaryContactName(organization) || 'Not provided', muted: !primaryContactName(organization) },
              { label: 'Website', value: organization?.vendorInfo?.website || 'Not provided', muted: !organization?.vendorInfo?.website },
              { label: 'Account approved', value: organization?.approvedAt ? new Date(organization.approvedAt).toLocaleDateString() : 'Not provided', muted: !organization?.approvedAt },
              { label: 'Data agreement', value: organization?.vendorInfo?.dataAgreement || 'Not provided', muted: !organization?.vendorInfo?.dataAgreement },
            ]}
            loading={summaryQuery.isLoading && !isDemoMode}
            refreshing={summaryQuery.isFetching}
            onRefresh={() => {
              queryClient.invalidateQueries({ queryKey: ['vendor-metrics-summary', brandKey] });
              queryClient.invalidateQueries({ queryKey: ['vendor-connectors', brandKey] });
              queryClient.invalidateQueries({ queryKey: ['vendor-ingestion-history', brandKey] });
              queryClient.invalidateQueries({ queryKey: ['vendor-map-violations', mapVendorId] });
              queryClient.invalidateQueries({ queryKey: ['vendor-rebate-files', orgKey] });
              queryClient.invalidateQueries({ queryKey: ['vendor-managed-brands', orgKey] });
            }}
          />
  );
  const sellThroughNode =
    sellThroughSharingEnabled && !isDemoMode && isActive && !!summary?.vendorId ? <VendorSellThroughSharingCount /> : null;
  // Plan-locked numbers: none on the vendor shell yet, so nothing to hide.
  const kpiNode = (
    <KpiRow
      kpis={kpis}
      loading={summaryQuery.isLoading && !isDemoMode}
      order={dash.widgets.find((w) => w.id === 'kpis')?.config.kpis}
      editing={dash.editing}
      onChange={dash.setKpis}
    />
  );
  const attentionNode = <AttentionQueueCard items={attention.items} truncatedCount={attention.truncatedCount} loading={!isDemoMode && summaryQuery.isLoading} />;
  const storesNode = <StoresCard stores={effectiveStores} loading={!isDemoMode && summaryQuery.isLoading} />;
  const rebateFilesNode = <RebateFilesCard records={effectiveRebates} loading={!isDemoMode && rebateHistoryQuery.isLoading} />;
  const managedBrandsNode = <ManagedBrandsCard brands={isDemoMode ? DEMO_MANAGED_BRANDS : managedBrands} loading={!isDemoMode && managedBrandsQuery.isLoading} />;
  const ingestionNode = (
    <IngestionFeedCard
      connector={effectiveConnector}
      jobs={effectiveJobs}
      summary={effectiveIngestionSummary}
      loading={!isDemoMode && (connectorsQuery.isLoading || ingestionHistoryQuery.isLoading)}
    />
  );
  const syncHistoryNode = (
    <SyncHistoryCard
      jobs={effectiveJobs}
      catalogTotal={isDemoMode ? 847 : summary?.totalProducts}
      loading={!isDemoMode && ingestionHistoryQuery.isLoading}
    />
  );
  const activityNode = <BrandActivityCard rows={activityRows} />;
  const widgetNodes: Record<string, React.ReactNode> = {
    kpis: kpiNode,
    sell_through_sharing: sellThroughNode,
    attention: attentionNode,
    stores: storesNode,
    rebate_files: rebateFilesNode,
    managed_brands: managedBrandsNode,
    ingestion_feed: ingestionNode,
    sync_history: syncHistoryNode,
    activity: activityNode,
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        p: '24px 28px 40px',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        // Every section is a flex item of this column, and the cards (overflow: hidden) have
        // min-height 0, so a height-constrained ancestor squeezed them to a sliver with the
        // content clipped (MOT-170). Sections keep their content height; the page scrolls.
        '& > *': { flexShrink: 0 },
      }}
    >
      {isDemoMode && (
        <Alert severity="info">
          <Typography variant="body1" fontWeight="bold" gutterBottom>Demo Mode</Typography>
          <Typography variant="body2">This is a preview with sample data. Vendors will see their actual catalog, store sync and ingestion data here.</Typography>
        </Alert>
      )}

      {!isDemoMode && isPendingApproval && !isActive && (
        <Alert severity="info">
          <Typography variant="body1" fontWeight="bold" gutterBottom>Application Under Review</Typography>
          <Typography variant="body2">We are reviewing your application. Below is a preview of what your dashboard will look like once approved.</Typography>
        </Alert>
      )}

      {!isDemoMode && summaryQuery.isError && (
        <Alert severity="warning">
          Unable to load your brand dashboard right now. Please try again later.
        </Alert>
      )}

      {!isDemoMode && (mapViolationsQuery.isError || rebateHistoryQuery.isError || managedBrandsQuery.isError) && (
        <Alert severity="warning">
          Some data could not be loaded right now
          {' ('}
          {[
            mapViolationsQuery.isError && 'MAP violations',
            rebateHistoryQuery.isError && 'rebate history',
            managedBrandsQuery.isError && 'managed brands',
          ]
            .filter(Boolean)
            .join(', ')}
          {'). '}
          Refresh to try again.
        </Alert>
      )}

      {organization && showApplicationCards && (
        <Card id="application">
          <CardContent>
            <Typography variant="h6" gutterBottom>Application Details</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(220px, 1fr))' }, gap: 2 }}>
              <Box>
                <Typography color="text.secondary" variant="caption">Company</Typography>
                <Typography variant="subtitle1">{organization?.vendorInfo?.companyName || organization?.name || '—'}</Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">Brand</Typography>
                <Typography variant="subtitle1">{organization?.vendorInfo?.brandName || organization?.vendorName || '—'}</Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">Website</Typography>
                <Typography variant="subtitle1">{organization?.vendorInfo?.website || 'Not provided'}</Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">Primary Contact</Typography>
                <Typography variant="subtitle1">{primaryContactName(organization) || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">{organization?.contactInfo?.primaryContact?.email || '—'}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {organization && showApplicationCards && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Approval Status &amp; Messages</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(220px, 1fr))' }, gap: 2, mb: 2 }}>
              <Box>
                <Typography color="text.secondary" variant="caption">Status</Typography>
                <Typography variant="subtitle1">{approvalRequest?.status || organization?.status || 'pending_approval'}</Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">Submitted</Typography>
                <Typography variant="subtitle1">{approvalRequest?.submittedAt ? new Date(approvalRequest.submittedAt).toLocaleString() : '—'}</Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">Reviewed</Typography>
                <Typography variant="subtitle1">{approvalRequest?.reviewedAt ? new Date(approvalRequest.reviewedAt).toLocaleString() : '—'}</Typography>
              </Box>
            </Box>

            {approvalRequest?.reviewNotes && <Alert severity="info" sx={{ mb: 2 }}>Reviewer Notes: {approvalRequest.reviewNotes}</Alert>}
            {approvalRequest?.rejectionReason && <Alert severity="error" sx={{ mb: 2 }}>Rejection Reason: {approvalRequest.rejectionReason}</Alert>}

            {dnsToken && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Domain verification</Typography>
                <Typography variant="body2">Add a TXT record on {dnsHost || 'your brand domain'} with this value:</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: brandHome.font.mono, wordBreak: 'break-all' }}>{dnsToken}</Typography>
                  <Button size="small" startIcon={<ContentCopy />} onClick={() => navigator.clipboard.writeText(dnsToken)} sx={{ textTransform: 'none', flexShrink: 0 }}>
                    Copy
                  </Button>
                </Stack>
              </Alert>
            )}

            {!approvalRequest && isPendingApproval && (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  We could not find your original submission details. Click below to create an approval record from your organization profile.
                </Alert>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button variant="outlined" onClick={() => ensureApprovalRequestMutation.mutate()} disabled={ensureApprovalRequestMutation.isPending}>
                    Create Approval Record
                  </Button>
                </Box>
              </>
            )}

            {(approvalRequest?.status === 'pending' || approvalRequest?.status === 'needs_info' || isPendingApproval) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  label="Message to Approvers"
                  multiline
                  minRows={3}
                  value={vendorNote}
                  onChange={(event) => setVendorNote(event.target.value)}
                  placeholder="Add context or notes for the review team"
                />
                {addVendorNoteMutation.isError && <Alert severity="error">{(addVendorNoteMutation.error as Error).message}</Alert>}
                {addVendorNoteMutation.isSuccess && <Alert severity="success">Your message has been sent to the approvers.</Alert>}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" disabled={vendorNote.trim().length < 3 || addVendorNoteMutation.isPending} onClick={() => addVendorNoteMutation.mutate(vendorNote.trim())}>
                    Send Message
                  </Button>
                </Box>
              </Box>
            )}

            {approvalRequest?.vendorNotes && approvalRequest.vendorNotes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Messages Sent</Typography>
                <List>
                  {approvalRequest.vendorNotes.map((note, index) => (
                    <ListItem key={`${note.createdAt}-${index}`} divider={index < approvalRequest.vendorNotes!.length - 1}>
                      <ListItemText primary={note.message} secondary={note.createdAt ? new Date(note.createdAt).toLocaleString() : '—'} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Never hide the dashboard behind pending/loading status — a pending or
          still-loading vendor sees the same layout with empty/zero states
          rather than the page disappearing and reappearing. */}
      <>
        {headerCardNode}
        {customizable && <DashboardEditBar controller={dash} />}
        {customizable && !dash.fixed ? (
          <WidgetGrid shell="vendor" controller={dash} nodes={widgetNodes} />
        ) : (
          <>
          {kpiNode}


          {/* Flag `sell_through_sharing`: only for an active vendor whose brand resolved (summary.vendorId). */}
          {sellThroughNode}

          {attentionNode}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(380px, 1fr))' }, gap: '18px', alignItems: 'start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', minWidth: 0 }}>
              {storesNode}
              {rebateFilesNode}
              {managedBrandsNode}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', minWidth: 0 }}>
              {ingestionNode}
              {syncHistoryNode}
              {activityNode}
            </Box>
          </Box>
          </>
        )}
      </>
    </Box>
  );
};
